import React, {useState} from 'react';
import b_ from 'b_';

import I18N from 'assets/i18n/brandlift-project';

import {useAudienceTargeting} from 'client/common/hooks/audience-targeting';
import AgeSlider from 'client/common/components/age-slider';
import {TargetingsProps} from 'client/common/components/audience-settings/types';
import {convertToLegoOptions} from 'client/common/components/audience-settings/helpers';
import {
    ExtendedAudienceCategoryId,
    AudienceCategoryValues,
    AgeCategory,
    GenderCategory
} from 'client/common/types';
import BlockInput from 'client/common/components/block-input';
import {Select, SelectWithTags} from 'client/common/components/lego';
import HorizontalSelector from 'client/common/components/horizontal-selector';
import {TargetingExtendedOptions, TargetingOptions} from 'client/common/utils/audience';

import {GeoCitySizeSelect} from './geo-citysize-select';

const b = b_.with('audience-settings');
const i18n = I18N.common.audienceSettings.audienceTargeting;

// eslint-disable-next-line complexity,max-statements
export default function TargetingTabExtended(props: TargetingsProps) {
    const {boxType, request, categories, interests, onChange, disabled} = props;

    const {
        selectedCountry,
        handleGeoChange,
        handleAgeChange,
        handleHaveCarChange,
        handleHaveChildrenChange,
        handleFamilyStatusChange,
        handleCohabitantsAmountChange,
        handleGenderChange,
        handleCitySizeChange
    } = useAudienceTargeting({
        boxType,
        request,
        categories,
        interests,
        onChange
    });

    const targetingValues = TargetingOptions.getTargetingValues(request);
    const targetingOptions = TargetingExtendedOptions.getTargetingOptions({
        categories,
        country: selectedCountry
    });

    const defaultValues = TargetingExtendedOptions.getDefaultValues({
        categories,
        boxType,
        selectedCountry
    });

    const [selectedGender, setSelectedGender] = useState(
        (targetingValues.gender ?? defaultValues.gender) as GenderCategory['value']
    );
    const [selectedAge, setSelectedAge] = useState<string[]>(
        targetingValues.age ?? defaultValues.age
    );
    const [selectedHaveCar, setSelectedHaveCar] = useState(
        targetingValues.have_car ?? defaultValues.haveCar
    );
    const [selectedHaveChildren, setSelectedHaveChildren] = useState(
        targetingValues.have_children ?? defaultValues.haveChildren
    );
    const [selectedCohabitantsAmount, setSelectedCohabitantsAmount] = useState(
        targetingValues.cohabitants_amount ?? defaultValues.cohabitantsAmount
    );
    const [selectedFamilyStatus, setSelectedFamilyStatus] = useState(
        targetingValues.family_status ?? defaultValues.familyStatus
    );

    const handleGenderSelect = (value: GenderCategory['value']) => {
        setSelectedGender(value);
        void handleGenderChange(value);
    };

    const handleAgeSelect = (value: AgeCategory['value']) => {
        setSelectedAge(value);
        void handleAgeChange(value);
    };

    const handleHaveCarSelect = (value: AudienceCategoryValues['have_car']) => {
        setSelectedHaveCar(value);
        void handleHaveCarChange(value);
    };

    const handleHaveChildrenSelect = (value: AudienceCategoryValues['have_children']) => {
        setSelectedHaveChildren(value);
        void handleHaveChildrenChange(value);
    };

    const handleCohabitantsAmountSelect = (value: AudienceCategoryValues['cohabitants_amount']) => {
        setSelectedCohabitantsAmount(value);
        void handleCohabitantsAmountChange(value);
    };

    const handleFamilyStatusSelect = (value: AudienceCategoryValues['family_status']) => {
        setSelectedFamilyStatus(value);
        void handleFamilyStatusChange(value);
    };

    const handleExtendedTargetingChange = (value: ExtendedAudienceCategoryId) => {
        if (value === 'have_car') {
            void handleHaveCarChange(selectedHaveCar);
        } else if (value === 'have_children') {
            void handleHaveChildrenChange(selectedHaveChildren);
        } else if (value === 'cohabitants_amount') {
            void handleCohabitantsAmountChange(selectedCohabitantsAmount);
        } else if (value === 'family_status') {
            void handleFamilyStatusChange(selectedFamilyStatus);
        }
    };

    const selectedCategoryId = TargetingExtendedOptions.getSelectedCategory(request);

    return (
        <>
            <div className={b('audience-type-wrapper', {with: 'margin'})}>
                <>
                    <BlockInput title={i18n.regionTitle}>
                        <GeoCitySizeSelect
                            handleGeoChange={handleGeoChange}
                            handleCitySizeChange={handleCitySizeChange}
                            disabled={disabled}
                            geoOptions={targetingOptions.geo}
                            defaultGeo={defaultValues.geo}
                            geoValues={targetingValues.geo}
                            citySizeOptions={targetingOptions.city_size}
                            defaultCitySize={defaultValues.citySize}
                            citySizeValues={targetingValues.city_size}
                        />
                    </BlockInput>
                    <div className={b('audience-type-wrapper', {type: 'gender'})}>
                        <BlockInput title={i18n.genderTitle}>
                            <SelectWithTags
                                onChange={e => handleGenderSelect(e.target.value)}
                                options={convertToLegoOptions(targetingOptions.gender)}
                                value={selectedGender}
                                size='m'
                                disabled={disabled}
                                minSelected={1}
                                width='max'
                                view='dl-clear'
                            />
                        </BlockInput>
                        <BlockInput title={i18n.ageTitle}>
                            <AgeSlider
                                age={selectedAge}
                                disabled={Boolean(disabled)}
                                onChange={handleAgeSelect}
                            />
                        </BlockInput>
                    </div>
                    <div style={{marginTop: '36px'}}>
                        <HorizontalSelector
                            options={TargetingExtendedOptions.getExtendedCategoryOptions()}
                            initialSelected={selectedCategoryId}
                            theme='default'
                            onChange={async val => handleExtendedTargetingChange(val)}
                            disabled={disabled}
                        />
                    </div>
                    {selectedCategoryId === 'have_car' && (
                        <BlockInput title={i18n.haveCarTitle}>
                            <Select
                                onChange={e => handleHaveCarSelect(e.target.value)}
                                options={convertToLegoOptions(targetingOptions.have_car)}
                                value={selectedHaveCar}
                                size='m'
                                disabled={disabled}
                                width='max'
                                view='dl-clear'
                            />
                        </BlockInput>
                    )}
                    {selectedCategoryId === 'have_children' && (
                        <BlockInput title={i18n.haveChildrenTitle}>
                            <Select
                                onChange={e => handleHaveChildrenSelect(e.target.value)}
                                options={convertToLegoOptions(targetingOptions.have_children)}
                                value={selectedHaveChildren}
                                size='m'
                                disabled={disabled}
                                width='max'
                                view='dl-clear'
                            />
                        </BlockInput>
                    )}
                    {selectedCategoryId === 'family_status' && (
                        <BlockInput title={i18n.familyStatusTitle}>
                            <Select
                                onChange={e => handleFamilyStatusSelect(e.target.value)}
                                options={convertToLegoOptions(targetingOptions.family_status)}
                                value={selectedFamilyStatus}
                                size='m'
                                disabled={disabled}
                                width='max'
                                view='dl-clear'
                            />
                        </BlockInput>
                    )}
                    {selectedCategoryId === 'cohabitants_amount' && (
                        <BlockInput title={i18n.cohabitantsAmountTitle}>
                            <Select
                                onChange={e => handleCohabitantsAmountSelect(e.target.value)}
                                options={convertToLegoOptions(targetingOptions.cohabitants_amount)}
                                value={selectedCohabitantsAmount}
                                size='m'
                                disabled={disabled}
                                width='max'
                                view='dl-clear'
                            />
                        </BlockInput>
                    )}
                </>
            </div>
        </>
    );
}
